import Vue from "vue";

//set Headers payload
const authHeader = process.env.VUE_APP_REQUEST_HEADER;
const headers = {};
headers[authHeader] = process.env.VUE_APP_HUB_API_KEY;

const base = process.env.VUE_APP_HUB_API_HOST + "api/updates";

function errorMessage(error, fallback) {
  if (error && error.body && (error.body.error || error.body.message)) {
    return error.body.error || error.body.message;
  }
  return fallback;
}

/**
 * Authoring the in-app updates - what the navbar bell shows every user.
 *
 * The read side of this lives in the `notifications` module, which is what the bell itself uses.
 * This one is the settings screen: writing an update and choosing who gets it.
 *
 * An update only reaches somebody through a UserUpdates row, so the recipients are written with the
 * update itself - a global one gets a row for every user, and stays that way when it is edited.
 */
export default {
  namespaced: true,
  state: () => ({
    loadingUpdates: false,
    savingUpdate: false,
    updatesError: null,
    // every update, newest first, each carrying its recipients and read count
    updates: [],
  }),
  mutations: {
    setLoadingUpdates(state, item) {
      state.loadingUpdates = item
    },
    setSavingUpdate(state, item) {
      state.savingUpdate = item
    },
    setUpdatesError(state, item) {
      state.updatesError = item
    },
    loadUpdates(state, items) {
      state.updates = items
    },
  },
  actions: {
    // The screen shows the recent ones, so the request asks for those: a global update carries a
    // recipient row per user, which the API returns with it.
    async fetchUpdates({ commit }, limit = 5) {
      commit("setLoadingUpdates", true);
      commit("setUpdatesError", null);

      try {
        const response = await Vue.http.get(base, {
          headers: headers,
          params: { limit: limit }
        });
        commit("loadUpdates", response.body);
      } catch (error) {
        console.error("Error fetching updates:", error);
        commit("setUpdatesError", errorMessage(error, "Nu s-au putut incarca update-urile."));
      } finally {
        commit("setLoadingUpdates", false);
      }
    },
    // payload: { title, subtitle, description, is_global, users: [id] }
    // users is required unless the update is global
    async createUpdate({ commit, dispatch }, payload) {
      commit("setSavingUpdate", true);

      try {
        await Vue.http.post(base, payload, { headers: headers });
        await dispatch("fetchUpdates");
        M.toast({ html: "Update trimis", classes: "bg-green-500" });
        return true;
      } catch (error) {
        console.error("Error creating update:", error);
        M.toast({
          html: errorMessage(error, "Update-ul nu a putut fi salvat."),
          classes: "bg-red-500"
        });
        return false;
      } finally {
        commit("setSavingUpdate", false);
      }
    },
    // payload: { id, ...changed fields }. Recipients are only touched when `users` is sent, and
    // the ones that stay keep their read state - a corrected typo does not re-notify anybody.
    async saveUpdate({ commit, dispatch }, payload) {
      commit("setSavingUpdate", true);

      const { id, ...fields } = payload;

      try {
        await Vue.http.put(base + "/" + id, fields, { headers: headers });
        await dispatch("fetchUpdates");
        M.toast({ html: "Update salvat", classes: "bg-green-500" });
        return true;
      } catch (error) {
        console.error("Error saving update " + id + ":", error);
        M.toast({
          html: errorMessage(error, "Update-ul nu a putut fi salvat."),
          classes: "bg-red-500"
        });
        return false;
      } finally {
        commit("setSavingUpdate", false);
      }
    },
    /** Removes the update and every recipient row pointing at it. */
    async deleteUpdate({ commit, dispatch }, id) {
      commit("setSavingUpdate", true);

      try {
        await Vue.http.delete(base + "/" + id, { headers: headers });
        await dispatch("fetchUpdates");
        M.toast({ html: "Update sters", classes: "bg-green-500" });
        return true;
      } catch (error) {
        console.error("Error deleting update " + id + ":", error);
        M.toast({
          html: errorMessage(error, "Update-ul nu a putut fi sters."),
          classes: "bg-red-500"
        });
        return false;
      } finally {
        commit("setSavingUpdate", false);
      }
    },
  }
}
